/**
 * Unterlagen Component
 * Displays and manages documents and study materials for students
 */

import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslatePipe, I18nStore } from '../../core/features/translations';
import { AuthService } from '../../services/auth.service';
import { ContactService } from '../../core/services/contact.service';
import {
  StudentDocumentService,
  StudentDocument,
} from '../../core/services/student-document.service';
import { ContactRead } from '../../core/models';
import { switchMap } from 'rxjs';
import {
  FilenameInputDialogComponent,
  FilenameInputDialogData,
  FilenameInputDialogResult,
} from './filename-input-dialog.component';

@Component({
  selector: 'app-unterlagen',
  standalone: true,
  imports: [CommonModule, TranslatePipe, MatDialogModule],
  template: `
    <div class="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in font-sans">
      <!-- Loading State -->
      @if (loading()) {
        <div class="flex flex-col items-center justify-center p-16 text-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div class="w-12 h-12 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin"></div>
          <p class="text-gray-600 text-base m-0">{{ 'portal.documents.loading' | translate }}</p>
        </div>
      }

      <!-- Error State -->
      @if (error() && !loading()) {
        <div class="flex flex-col items-center justify-center p-16 text-center gap-4 bg-white rounded-2xl border border-red-100 shadow-sm">
          <span class="material-symbols-outlined text-red-500 text-5xl">error</span>
          <p class="text-gray-700 text-base m-0 font-semibold max-w-lg leading-relaxed">{{ error() }}</p>
          <button 
            type="button"
            (click)="loadContactAndDocuments()"
            class="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 transition-all focus:outline-none"
          >
            <span class="material-symbols-outlined text-lg">refresh</span>
            {{ 'portal.degrees.retry' | translate }}
          </button>
        </div>
      }

      <!-- Main Content -->
      @if (!loading() && !error()) {
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <!-- Upload Section -->
          <div class="lg:col-span-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-8 transition-all">
            <div class="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
              <h2 class="text-lg font-bold text-gray-900 m-0 flex items-center gap-2">
                <span class="material-symbols-outlined text-blue-600 text-2xl">upload_file</span>
                {{ 'portal.documents.upload.title' | translate }}
              </h2>
              @if (documents().length > 0) {
                <span class="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold tracking-wide">
                  {{ documents().length }} {{ 'portal.documents.list.countSuffix' | translate }}
                </span>
              }
            </div>

            <!-- Document Category Selection Dropdown -->
            <div class="mb-5 flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                <span class="material-symbols-outlined text-sm text-blue-600">category</span>
                <span>Dokument-Kategorie</span>
                <span class="text-red-500 font-bold">*</span>
              </label>
              <select
                [value]="selectedCategory()"
                (change)="onCategoryChange($event)"
                class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer"
              >
                <option value="" disabled>-- Bitte Kategorie auswählen --</option>
                <option value="admission">Zulassungsunterlagen</option>
              </select>
              @if (!selectedCategory()) {
                <p class="text-xs text-amber-700 font-medium m-0 flex items-center gap-1 mt-0.5">
                  <span class="material-symbols-outlined text-xs">info</span>
                  Bitte wählen Sie eine Kategorie für den Upload
                </p>
              }
            </div>

            <!-- Upload Dropzone -->
            <div
              class="relative rounded-2xl p-6 border-2 border-dashed transition-all duration-200 flex flex-col items-center text-center gap-4 overflow-hidden"
              [class.border-blue-500]="isDragging() && selectedCategory()"
              [class.bg-blue-50]="isDragging() && selectedCategory()"
              [class.border-gray-200]="!isDragging() && selectedCategory()"
              [class.hover:border-blue-300]="!uploading() && selectedCategory()"
              [class.hover:bg-blue-50/30]="!uploading() && selectedCategory()"
              [class.cursor-pointer]="!uploading() && selectedCategory()"
              [class.opacity-60]="!selectedCategory()"
              [class.bg-gray-50]="!selectedCategory()"
              [class.border-gray-200]="!selectedCategory()"
              [class.cursor-not-allowed]="!selectedCategory()"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
              (click)="onDropzoneClick(fileInput)"
            >
              <input
                #fileInput
                type="file"
                class="hidden"
                (change)="onFileSelected($event)"
                [disabled]="uploading() || !selectedCategory()"
              />

              <div class="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center transition-all shadow-sm">
                <span class="material-symbols-outlined text-3xl">cloud_upload</span>
              </div>
              
              <div class="flex flex-col gap-1">
                <p class="text-gray-900 text-sm font-bold m-0 leading-tight">
                  {{ 'portal.documents.upload.dragDrop' | translate }}
                </p>
                <p class="text-gray-500 text-xs font-medium uppercase tracking-wider m-0">
                  {{ 'portal.documents.upload.or' | translate }}
                </p>
              </div>

              <button
                type="button"
                class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm shadow-sm transition-all hover:bg-blue-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                [disabled]="uploading() || !selectedCategory()"
              >
                @if (uploading()) {
                  <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{{ 'portal.documents.upload.uploading' | translate }}</span>
                } @else {
                  <span class="material-symbols-outlined text-lg">add_circle</span>
                  <span>{{ 'portal.documents.upload.selectFile' | translate }}</span>
                }
              </button>
              
              <div class="flex items-center gap-1.5 text-xs text-gray-500 font-medium px-2.5 py-1 bg-gray-100/70 rounded-lg">
                <span class="material-symbols-outlined text-xs text-gray-500">info</span>
                <span>{{ 'portal.documents.upload.maxSize' | translate }}: {{ maxFileSizeFormatted() }}</span>
              </div>

              <!-- Success Overlay -->
              @if (uploadSuccess()) {
                <div class="absolute inset-0 bg-white/95 flex flex-col items-center justify-center animate-fade-in backdrop-blur-sm z-10 p-4">
                  <div class="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-3 text-green-600 shadow-sm animate-pulse">
                    <span class="material-symbols-outlined text-3xl">check_circle</span>
                  </div>
                  <p class="text-green-800 font-bold text-base m-0 text-center">{{ uploadProgress() }}</p>
                </div>
              }
            </div>
          </div>

          <!-- Documents List Section -->
          <div class="lg:col-span-8 flex flex-col gap-4">
            @if (documents().length === 0) {
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
                <div class="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100 text-gray-400">
                  <span class="material-symbols-outlined text-4xl">folder_open</span>
                </div>
                <h3 class="text-gray-900 text-lg font-bold m-0 mb-1.5">{{ 'portal.documents.list.empty' | translate }}</h3>
                <p class="text-gray-500 max-w-sm mx-auto text-sm leading-relaxed m-0">{{ 'portal.documents.list.emptyHint' | translate }}</p>
              </div>
            } @else {
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @for (doc of documents(); track doc.name) {
                  <div class="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group flex flex-col justify-between gap-4">
                    <div class="flex items-start gap-3.5">
                      <div class="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                        <span class="material-symbols-outlined text-2xl">{{ getFileIcon(doc.name) }}</span>
                      </div>

                      <div class="flex-1 min-w-0">
                        <div class="text-sm font-bold text-gray-900 truncate mb-1" [title]="doc.name">
                          {{ doc.name }}
                        </div>
                        <div class="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          <span class="px-2 py-0.5 bg-gray-100 rounded text-gray-700 font-medium">
                            {{ getFileSize(doc.size) }}
                          </span>
                          @if (doc.category === 'admission') {
                            <span class="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold text-[11px]">
                              Zulassung
                            </span>
                          }
                          <span class="truncate">{{ formatDate(doc.last_modified) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons: Always clearly visible with Material Symbols -->
                    <div class="flex items-center justify-end gap-2 pt-2 border-t border-gray-50">
                      <button
                        type="button"
                        (click)="downloadDocument(doc)"
                        class="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-600 hover:text-white active:scale-95 transition-all"
                        [title]="'portal.documents.download' | translate"
                      >
                        <span class="material-symbols-outlined text-base">download</span>
                        <span>Download</span>
                      </button>

                      <button
                        type="button"
                        (click)="deleteDocument(doc)"
                        class="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-600 hover:text-white active:scale-95 transition-all"
                        [title]="'portal.documents.delete.button' | translate"
                      >
                        <span class="material-symbols-outlined text-base">delete</span>
                        <span>Löschen</span>
                      </button>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnterlagenComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly contactService = inject(ContactService);
  private readonly documentService = inject(StudentDocumentService);
  private readonly dialog = inject(MatDialog);
  private readonly i18n = inject(I18nStore);

  // Constants
  readonly maxFileSize = 50 * 1024 * 1024; // 50MB in bytes

  // Signals
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly contactId = signal<number | null>(null);
  readonly documents = signal<StudentDocument[]>([]);
  readonly uploading = signal<boolean>(false);
  readonly uploadProgress = signal<string>('');
  readonly uploadSuccess = signal<boolean>(false);
  readonly selectedFile = signal<File | null>(null);
  readonly isDragging = signal<boolean>(false);
  readonly selectedCategory = signal<string>('');

  // Computed
  readonly maxFileSizeFormatted = computed(() => {
    const mb = this.maxFileSize / (1024 * 1024);
    return `${mb}MB`;
  });

  ngOnInit(): void {
    this.loadContactAndDocuments();
  }

  /**
   * Handle dropdown category selection
   */
  onCategoryChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedCategory.set(select.value);
  }

  /**
   * Handle dropzone click
   */
  onDropzoneClick(fileInput: HTMLInputElement): void {
    if (!this.selectedCategory() || this.uploading()) {
      return;
    }
    fileInput.click();
  }

  /**
   * Load contact ID and then load documents
   */
  public loadContactAndDocuments(): void {
    this.loading.set(true);
    this.error.set(null);

    // Get user info from auth service
    const userInfo = this.authService.getUserInfo();

    if (!userInfo) {
      this.error.set(this.i18n.t('portal.documents.error.noUser'));
      this.loading.set(false);
      return;
    }

    const userEmail = userInfo.sub || userInfo.email;

    if (!userEmail) {
      this.error.set(this.i18n.t('portal.documents.error.noEmail'));
      this.loading.set(false);
      return;
    }

    // Find contact by email, then load documents
    this.contactService
      .getContactByEmail(userEmail)
      .pipe(
        switchMap((contact: ContactRead) => {
          this.contactId.set(contact.contact_id);
          return this.documentService.listDocuments(contact.contact_id, false);
        }),
      )
      .subscribe({
        next: (docs: StudentDocument[]) => {
          this.documents.set(docs);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          console.error('❌ Error loading documents:', err);
          this.error.set(this.i18n.t('portal.documents.error.loading'));
          this.loading.set(false);
        },
      });
  }

  /**
   * Handle file selection from input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.selectedFile.set(file);
      this.showFilenameDialog(file);

      // Reset input after reading the file to allow re-uploading the same file
      input.value = '';
    }
  }

  /**
   * Handle drag over
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.selectedCategory() || this.uploading()) {
      return;
    }
    this.isDragging.set(true);
  }

  /**
   * Handle drag leave
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  /**
   * Handle file drop
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (!this.selectedCategory() || this.uploading()) {
      return;
    }

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.selectedFile.set(event.dataTransfer.files[0]);
      this.showFilenameDialog(event.dataTransfer.files[0]);
    }
  }

  /**
   * Show filename input dialog
   */
  private showFilenameDialog(file: File): void {
    // Validate file size
    if (file.size > this.maxFileSize) {
      this.error.set(
        this.i18n.t('portal.documents.error.fileTooBig', { maxSize: this.maxFileSizeFormatted() }),
      );
      return;
    }

    // Validate file is not empty
    if (file.size === 0) {
      this.error.set(this.i18n.t('portal.documents.error.fileEmpty'));
      return;
    }

    // Extract filename without extension for suggestion
    const filenameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

    const dialogData: FilenameInputDialogData = {
      originalFilename: file.name,
      suggestedFilename: filenameWithoutExt,
    };

    const dialogRef = this.dialog.open(FilenameInputDialogComponent, {
      width: '500px',
      disableClose: false,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: FilenameInputDialogResult | undefined) => {
      if (result && !result.cancelled) {
        this.uploadFile(file, result.filename);
      } else {
        // User cancelled, reset state
        this.selectedFile.set(null);
      }
    });
  }

  /**
   * Upload file to server
   */
  private uploadFile(file: File, customFilename?: string): void {
    const contactId = this.contactId();
    if (!contactId) {
      this.error.set(this.i18n.t('portal.documents.error.noContactId'));
      return;
    }

    this.uploading.set(true);
    this.uploadProgress.set(this.i18n.t('portal.documents.upload.uploading'));
    this.uploadSuccess.set(false);
    this.error.set(null);

    const category = this.selectedCategory() || 'admission';
    this.documentService.uploadDocument(contactId, file, false, customFilename, category).subscribe({
      next: (response) => {
        console.log('✅ Document uploaded successfully:', response);
        this.uploading.set(false);
        this.uploadSuccess.set(true);
        this.uploadProgress.set(this.i18n.t('portal.documents.upload.success'));
        this.selectedFile.set(null);

        // Reload documents list
        this.loadContactAndDocuments();

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.uploadSuccess.set(false);
          this.uploadProgress.set('');
        }, 3000);
      },
      error: (err: unknown) => {
        console.error('❌ Error uploading document:', err);
        this.uploading.set(false);
        this.uploadSuccess.set(false);
        this.uploadProgress.set('');
        this.error.set(this.i18n.t('portal.documents.error.uploadFailed'));

        // Clear error after 5 seconds
        setTimeout(() => {
          this.error.set(null);
        }, 5000);
      },
    });
  }

  /**
   * Download a document
   */
  downloadDocument(doc: StudentDocument): void {
    const contactId = this.contactId();
    if (!contactId) return;

    // Use browser to download file
    const url = this.documentService.getDocumentDownloadUrl(contactId, doc.name);
    window.open(url, '_blank');
  }

  /**
   * Delete a document
   */
  deleteDocument(doc: StudentDocument): void {
    const contactId = this.contactId();
    if (!contactId) return;

    if (!confirm(this.i18n.t('portal.documents.delete.confirm', { name: doc.name }))) {
      return;
    }

    this.documentService.deleteDocument(contactId, doc.name).subscribe({
      next: () => {
        console.log('✅ Document deleted successfully');
        // Reload documents list
        this.loadContactAndDocuments();
      },
      error: (err: unknown) => {
        console.error('❌ Error deleting document:', err);
        this.error.set(this.i18n.t('portal.documents.error.deleteFailed'));
      },
    });
  }

  /**
   * Get formatted file size
   */
  getFileSize(bytes: number): string {
    return this.documentService.formatFileSize(bytes);
  }

  /**
   * Get file icon
   */
  getFileIcon(filename: string): string {
    return this.documentService.getFileIcon(filename);
  }

  /**
   * Format date
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }
}
